
import React, { useState, useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import { useApp } from '../context/AppContext';
import { Navigate, useNavigate, useParams, Link } from 'react-router-dom';
import { 
  ArrowLeft, Save, Sparkles, AlertCircle, FileText, Layout, 
  Image as ImageIcon, Loader2, RefreshCcw, Search, Upload, X 
} from 'lucide-react';
import { GoogleGenAI } from "@google/genai";

declare var process: any;

const CATEGORIES = ['Community', 'Indian Compliance', 'UAE Compliance', 'Accounting', 'Process'] as const;

const EditPost: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const { currentUser, posts, updatePost } = useApp();
  const navigate = useNavigate();
  const fileInputRef = useRef<HTMLInputElement>(null);
  
  const post = posts.find(p => p.id === id);
  
  const [title, setTitle] = useState('');
  const [imageUrl, setImageUrl] = useState('');
  const [category, setCategory] = useState<typeof CATEGORIES[number]>('Community');
  const [excerpt, setExcerpt] = useState('');
  const [content, setContent] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isVisualLoading, setIsVisualLoading] = useState(false);

  useEffect(() => {
    if (post) {
      setTitle(post.title);
      setImageUrl(post.imageUrl || '');
      setCategory(post.category as any);
      setExcerpt(post.excerpt);
      setContent(post.content);
    }
  }, [post]);

  if (!currentUser) return <Navigate to="/blog" replace />;
  if (!post || (post.authorId !== currentUser.id && !currentUser.isAdmin)) {
    return <Navigate to="/profile" replace />;
  }

  const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setImageUrl(reader.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const generateAiVisual = async () => {
    setIsVisualLoading(true);
    try {
      const ai = new GoogleGenAI({ apiKey: process.env.API_KEY });
      const promptGenerator = await ai.models.generateContent({
        model: 'gemini-3-flash-preview',
        contents: `Create a professional, high-fidelity technical illustration prompt for an image generation model. The subject is: ${title}. Style: "Minimalist SaaS dashboard style, clean lines, professional financial color palette, 3D abstract isometric, slate and indigo accents". Return ONLY the prompt text.`,
      });
      const visualPrompt = promptGenerator.text;

      const response = await ai.models.generateContent({
        model: 'gemini-2.5-flash-image',
        contents: { parts: [{ text: visualPrompt || `A professional financial visual for ${title}` }] },
        config: { imageConfig: { aspectRatio: "16:9" } },
      });

      const parts = response.candidates?.[0]?.content?.parts;
      if (parts) {
        for (const part of parts) {
          if (part.inlineData) {
            setImageUrl(`data:image/png;base64,${part.inlineData.data}`);
            break;
          }
        }
      }
    } catch (err) {
      console.error("Visual Engine Error:", err);
    } finally {
      setIsVisualLoading(false);
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!title.trim() || !content.trim() || !excerpt.trim()) return;

    setIsSubmitting(true);
    setTimeout(() => {
      updatePost(post.id, {
        title,
        imageUrl,
        category,
        excerpt,
        content,
        readingTime: `${Math.max(1, Math.round(content.split(' ').length / 200))} min read`
      });
      setIsSubmitting(false);
      navigate('/profile');
    }, 800);
  };

  return (
    <div className="pt-40 pb-32 min-h-screen bg-slate-50 text-slate-900">
      <div className="max-w-4xl mx-auto px-6">
        <Link to="/profile" className="inline-flex items-center gap-2 text-slate-500 hover:text-indigo-600 font-semibold mb-12 transition-colors group">
          <ArrowLeft size={18} className="group-hover:-translate-x-1 transition-transform" />
          Cancel and return to profile
        </Link>

        <motion.div 
          initial={{ opacity: 0, y: 20 }} 
          animate={{ opacity: 1, y: 0 }} 
          className="bg-white border border-slate-200 rounded-[3.5rem] p-8 md:p-16 shadow-2xl relative overflow-hidden"
        >
          <div className="absolute top-0 right-0 p-12 text-indigo-50 pointer-events-none">
            <Layout size={180} strokeWidth={0.5} />
          </div>

          <header className="mb-12 relative z-10">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-12 h-12 rounded-2xl bg-indigo-50 flex items-center justify-center text-indigo-600 border border-indigo-100 shadow-sm">
                <FileText size={24} />
              </div>
              <div>
                <h1 className="text-4xl font-display font-bold text-slate-900">Edit Your Insight</h1>
                <p className="text-slate-500 text-sm mt-1">Refine and update your published content.</p>
              </div>
            </div>
          </header>

          <form onSubmit={handleSubmit} className="space-y-8 relative z-10">
            <div className="space-y-2">
              <label className="text-xs font-bold text-slate-400 uppercase tracking-widest ml-1">Headline</label>
              <input 
                type="text" 
                required 
                value={title} 
                onChange={(e) => setTitle(e.target.value)} 
                placeholder="Enter a compelling title..."
                className="w-full bg-slate-50 border border-slate-200 rounded-2xl px-6 py-4 text-slate-900 text-xl focus:border-indigo-500 outline-none transition-all font-display font-bold placeholder:text-slate-400 focus:bg-white" 
              />
            </div>

            <div className="space-y-4">
              <label className="text-xs font-bold text-slate-400 uppercase tracking-widest ml-1">Featured Visual</label>
              <div className="p-6 rounded-3xl bg-slate-50 border border-slate-200 space-y-6">
                {imageUrl ? (
                  <div className="relative group rounded-2xl overflow-hidden border border-slate-200 aspect-video">
                    <img src={imageUrl} alt="Draft Visual" className="w-full h-full object-cover" />
                    <div className="absolute inset-0 bg-slate-900/60 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-4">
                      <button type="button" onClick={generateAiVisual} className="p-3 bg-white text-slate-900 rounded-xl font-bold text-xs flex items-center gap-2 shadow-xl"><RefreshCcw size={16} /> Regenerate</button>
                      <button type="button" onClick={() => setImageUrl('')} className="p-3 bg-red-500 text-white rounded-xl font-bold text-xs flex items-center gap-2 shadow-xl"><X size={16} /> Remove</button>
                    </div>
                  </div>
                ) : (
                  <div className="border-2 border-dashed border-slate-200 rounded-2xl p-10 flex flex-col items-center justify-center text-center gap-6 group hover:border-indigo-200 transition-colors bg-white">
                    <div className="w-16 h-16 rounded-2xl bg-indigo-50 flex items-center justify-center text-indigo-400 group-hover:text-indigo-600 transition-colors">
                      {isVisualLoading ? <Loader2 className="animate-spin" /> : <ImageIcon size={32} />}
                    </div>
                    <div className="space-y-1">
                      <p className="text-slate-900 font-bold">{isVisualLoading ? 'Synthesizing...' : 'Architect Visual'}</p>
                      <p className="text-slate-400 text-xs px-10">AI engine synthesis or manual upload.</p>
                    </div>
                    <div className="flex flex-wrap items-center justify-center gap-4">
                      <button type="button" onClick={generateAiVisual} disabled={isVisualLoading} className="px-6 py-3 bg-indigo-600 hover:bg-indigo-700 text-white font-bold rounded-xl text-[10px] uppercase tracking-widest flex items-center gap-2 transition-all">
                        {isVisualLoading ? 'Synthesizing...' : <><Sparkles size={14} /> AI Engine</>}
                      </button>
                      <button type="button" onClick={() => fileInputRef.current?.click()} className="px-6 py-3 bg-white hover:bg-slate-50 text-slate-600 font-bold rounded-xl text-[10px] uppercase tracking-widest flex items-center gap-2 border border-slate-200 transition-all">
                        <Upload size={14} /> Upload
                      </button>
                      <input type="file" ref={fileInputRef} onChange={handleFileUpload} accept="image/*" className="hidden" />
                    </div>
                  </div>
                )}
                <div className="relative">
                  <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" size={14} />
                  <input 
                    type="url" 
                    value={imageUrl.startsWith('data:') ? '' : imageUrl} 
                    onChange={(e) => setImageUrl(e.target.value)} 
                    placeholder="Institutional asset URL..." 
                    className="w-full pl-10 pr-4 py-3 bg-white border border-slate-200 rounded-xl text-slate-600 text-xs focus:border-indigo-500 outline-none placeholder:text-slate-400"
                  />
                </div>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              <div className="space-y-2">
                <label className="text-xs font-bold text-slate-400 uppercase tracking-widest ml-1">Category</label>
                <div className="relative">
                  <select 
                    value={category} 
                    onChange={(e) => setCategory(e.target.value as any)} 
                    className="w-full bg-slate-50 border border-slate-200 rounded-2xl px-6 py-4 text-slate-900 focus:border-indigo-500 outline-none transition-all appearance-none cursor-pointer focus:bg-white"
                  >
                    {CATEGORIES.map(cat => <option key={cat} value={cat}>{cat}</option>)}
                  </select>
                  <div className="absolute right-6 top-1/2 -translate-y-1/2 pointer-events-none text-slate-400">
                    <Sparkles size={16} />
                  </div>
                </div>
              </div>
              <div className="flex items-center p-6 bg-indigo-50 border border-indigo-100 rounded-2xl text-slate-600 text-xs leading-relaxed">
                <AlertCircle size={18} className="text-indigo-600 shrink-0 mr-4" />
                Updating this post will refresh its timestamp for current readers.
              </div>
            </div>

            <div className="space-y-2">
              <label className="text-xs font-bold text-slate-400 uppercase tracking-widest ml-1">Summary Excerpt</label>
              <textarea 
                required 
                value={excerpt} 
                onChange={(e) => setExcerpt(e.target.value)} 
                placeholder="A brief teaser for the blog feed..."
                className="w-full bg-slate-50 border border-slate-200 rounded-2xl px-6 py-4 text-slate-600 focus:border-indigo-500 outline-none transition-all resize-none min-h-[100px] placeholder:text-slate-400 focus:bg-white" 
                rows={3} 
              />
            </div>

            <div className="space-y-2">
              <label className="text-xs font-bold text-slate-400 uppercase tracking-widest ml-1">Insight Content</label>
              <textarea 
                required 
                value={content} 
                onChange={(e) => setContent(e.target.value)} 
                placeholder="Share your full professional insight here..."
                className="w-full bg-slate-50 border border-slate-200 rounded-2xl px-6 py-5 text-slate-900 focus:border-indigo-500 outline-none transition-all min-h-[450px] leading-relaxed font-sans placeholder:text-slate-400 focus:bg-white" 
              />
            </div>

            <button 
              type="submit" 
              disabled={isSubmitting} 
              className="w-full py-5 bg-indigo-600 hover:bg-indigo-700 text-white font-bold rounded-2xl shadow-xl shadow-indigo-600/30 flex items-center justify-center gap-3 transition-all transform active:scale-[0.99] disabled:opacity-70 disabled:cursor-not-allowed"
            >
              {isSubmitting ? 'Syncing Changes...' : 'Save and Update Insight'}
              {!isSubmitting && <Save size={20} />}
            </button>
          </form>
        </motion.div>
      </div>
    </div>
  );
};

export default EditPost;
